import React, { useEffect, useRef, useState } from 'react';
import { ColorPicker } from 'antd';
import type { ColorPickerProps } from 'antd';
import { colors, typography } from '../../theme';
import { Fill } from '../utils/fill';
import { AngleControl } from './angle-control';
import { useBrandKit } from '../../brand-kit';

type AntColor = Parameters<NonNullable<ColorPickerProps['onChange']>>[0];

type Props = {
  value: Fill;
  onPreview: (fill: Fill) => void;
  onCommit: (fill: Fill) => void;
  label?: string;
  gradient?: boolean;
};

const toAntdValue = (fill: Fill) =>
  fill.kind === 'solid' ? fill.color : fill.stops.map(s => ({ color: s.color, percent: Math.round(s.offset * 100) }));

export const FillControl: React.FC<Props> = ({ value, onPreview, onCommit, label, gradient = true }) => {
  const { colors: brandColors } = useBrandKit();

  // Keep picker edits local until the user closes it.
  // Live changes update the buffer and preview on Fabric, but do not update the model.
  // This keeps a full picker session as a single undo step instead of one snapshot per change.
  const [buffer, setBuffer] = useState<Fill>(value);

  // Tracks whether the user changed something during the current picker session.
  const dirty = useRef(false);

  // Re-sync when the model changes outside the picker, such as a new selection or a committed value.
  useEffect(() => setBuffer(value), [value]);

  const preview = (fill: Fill) => {
    setBuffer(fill);
    dirty.current = true;
    onPreview(fill);
  };

  const handleOpenChange = (open: boolean) => {
    if (open || !dirty.current) return;
    dirty.current = false;
    onCommit(buffer);
  };

  const toFill = (color: AntColor): Fill => {
    if (color.isGradient()) {
      const stops = color.getColors().map(s => ({ offset: s.percent / 100, color: s.color.toRgbString() }));
      return { kind: 'linear', angle: buffer.kind === 'linear' ? buffer.angle : 0, stops };
    }
    return { kind: 'solid', color: color.toRgbString() };
  };

  const presets: ColorPickerProps['presets'] = brandColors?.length
    ? [{ label: 'Colores de marca', colors: brandColors }]
    : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      <ColorPicker
        mode={gradient ? ['single', 'gradient'] : 'single'}
        value={toAntdValue(buffer)}
        size='small'
        showText={false}
        presets={presets}
        onChange={color => preview(toFill(color))}
        onOpenChange={handleOpenChange}
        panelRender={panel => (
          <>
            {panel}
            {gradient && buffer.kind === 'linear' && (
              <div style={{ paddingTop: '8px' }}>
                <AngleControl
                  value={buffer.angle}
                  onPreview={angle => preview({ ...buffer, angle })}
                  onCommit={angle => preview({ ...buffer, angle })}
                />
              </div>
            )}
          </>
        )}
      />

      {label && (
        <span
          style={{ fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.ui, color: colors.textMuted }}
        >
          {label}
        </span>
      )}
    </div>
  );
};
